import { PDFDocument } from 'pdf-lib';

/** Last segment of the URL, which is the label's file name: how `failed` names it. */
export const labelName = (url) => {
  try {
    return new URL(url).pathname.split('/').pop() || url;
  } catch {
    return url;
  }
};

/**
 * Downloads one label.
 *
 * `fetch` does not reject on an HTTP error, so without this check the body of a
 * 403 — an XML document — reached `pdf-lib`, which reported it as an invalid
 * PDF and buried the real cause. A blocked request (CORS) rejects instead, and
 * carries no status.
 */
const fetchLabelBytes = async (url) => {
  let response;
  try {
    response = await fetch(url);
  } catch {
    throw new Error(`No se pudo pedir la etiqueta ${labelName(url)}`);
  }

  if (!response.ok) {
    throw new Error(`No se pudo leer la etiqueta ${labelName(url)} (${response.status})`);
  }

  return response.arrayBuffer();
};

/**
 * Merges the labels that can be read into a single PDF, and says which could
 * not.
 *
 * One unreadable label used to cancel the whole download. Now it is left out
 * and named, so 38 of 40 labels still reach the printer. It only throws when
 * none of them could be read, with the first reason.
 *
 * @param {string[]} urls
 * @param {object} [options]
 * @param {(done: number, total: number) => void} [options.onProgress] - Called
 *   as each label arrives.
 * @returns {Promise<{blob: Blob, failed: Array<{name: string, message: string}>}|null>}
 */
export const mergeLabelPdfs = async (urls, { onProgress } = {}) => {
  if (!urls || urls.length === 0) return null;

  let done = 0;
  const fetched = await Promise.allSettled(
    urls.map((url) =>
      fetchLabelBytes(url).finally(() => {
        done += 1;
        onProgress?.(done, urls.length);
      })
    )
  );

  const merged = await PDFDocument.create();
  const failed = [];

  for (let index = 0; index < fetched.length; index += 1) {
    const outcome = fetched[index];
    const name = labelName(urls[index]);

    if (outcome.status === 'rejected') {
      failed.push({ name, message: outcome.reason?.message || `No se pudo leer la etiqueta ${name}` });
      continue;
    }

    try {
      const pdfDoc = await PDFDocument.load(outcome.value);
      const pages = await merged.copyPages(pdfDoc, pdfDoc.getPageIndices());
      pages.forEach((page) => merged.addPage(page));
    } catch {
      failed.push({ name, message: `La etiqueta ${name} no es un PDF válido` });
    }
  }

  if (failed.length === urls.length) throw new Error(failed[0].message);

  const bytes = await merged.save();
  return { blob: new Blob([bytes], { type: 'application/pdf' }), failed };
};

/**
 * Merges every label into a single PDF.
 *
 * @param {string[]} urls
 * @returns {Promise<Blob|null>}
 */
export const mergePDFSFromUrls = async (urls) => {
  if (!urls || urls.length === 0) return null;

  const pdfBytesArray = await Promise.all(urls.map(fetchLabelBytes));

  const mergedPDFDoc = await PDFDocument.create();
  for (const pdfBytes of pdfBytesArray) {
    const pdfDoc = await PDFDocument.load(pdfBytes);
    const copiedPages = await mergedPDFDoc.copyPages(pdfDoc, pdfDoc.getPageIndices());
    copiedPages.forEach((page) => mergedPDFDoc.addPage(page));
  }

  const mergedPdfBytes = await mergedPDFDoc.save();
  return new Blob([mergedPdfBytes], { type: "application/pdf" });
};
